import { groupErrors } from './ldapPropTypes';

import { PureComponent } from 'react';
import {
  makeSelectGroupData,
  makeSelectGroupDataLoading,
  makeSelectRoles,
} from './selectors';

import { Button } from 'components/Button/Button';
import FormErrorMessage from 'components/FormErrorMessage';
import SelectField from 'components/SelectField';
import SelectInputField from 'components/SelectInputField';
import { Map as ImmutableMap } from 'immutable';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Field } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import { RolesSelectOption } from '../RolesSelectOption';
import RoleTextField from './RoleTextField';
import { groupSearchDataChange } from './actions';
import groupMenuRenderer from './groupMenuRenderer';

let searchDebounce = null;

class RenderRoleAssignments extends PureComponent {
  static propTypes = {
    configurationId: PropTypes.string.isRequired,
    fields: PropTypes.object,
    groupData: ImmutablePropTypes.list,
    groupDataLoading: PropTypes.number,
    groupErrors: groupErrors,
    meta: PropTypes.object,
    msg: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onClearGroupError: PropTypes.func.isRequired,
    onGroupSearchDataChange: PropTypes.func,
    roles: ImmutablePropTypes.list.isRequired,
  };

  state = {
    inputValue: '',
  };

  componentDidMount() {
    const { fields } = this.props;
    if (!fields.length) {
      fields.push(new ImmutableMap({ groupName: 'any' }));
    }
  }

  getGroupOptions = () => {
    const { groupData } = this.props;
    if (!groupData) {
      return [];
    }
    return groupData
      .map((option) => ({
        value: option.dn,
        label: option.dn,
      }))
      .toJS();
  };

  handleAddRole = handleEvent(() => {
    this.props.fields.push(new ImmutableMap());
    this.props.onChange();
  });

  handleRemoveRole = handleEvent((index) => () => {
    this.props.fields.remove(index);
    this.props.onChange();
  });

  handleGroupFilterChange = (index) =>
    handleEvent((value) => {
      const { onGroupSearchDataChange, configurationId } = this.props;
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
      this.setState({ inputValue: value });
      searchDebounce = setTimeout(() => {
        onGroupSearchDataChange({ value, id: configurationId, index });
      }, 600);
      return value;
    });

  handleGroupChange = (index) =>
    handleEvent(() => {
      const { onChange, onClearGroupError } = this.props;
      onChange();
      onClearGroupError({ index });
    });

  handleGroupFocus = (index) =>
    handleEvent(() => {
      const { onGroupSearchDataChange, configurationId } = this.props;
      onGroupSearchDataChange({ value: '', id: configurationId, index });
      this.setState({ inputValue: '' });
    });

  handleGroupBlur = handleEvent((newValue) => {
    this.setState({ inputValue: newValue });
  });

  render() {
    const {
      fields,
      msg,
      meta: { error },
      groupDataLoading,
      groupErrors,
      roles,
    } = this.props;
    const rolesOptions = roles.toJS().map((role) => ({
      value: role.id,
      label: role.name,
      description: role.description,
    }));
    const { inputValue } = this.state;
    const groupOptions = this.getGroupOptions();
    return (
      <div>
        <table className='mb-0 table w-auto'>
          <thead>
            <tr>
              <th className='ldap-role__cell' />
              <th className='ldap-role__cell-role'>
                {get(msg, 'configurations.roleAssignments.role')}
              </th>
              <th className='ldap-role__cell' />
            </tr>
          </thead>
          <tbody>
            {fields.map((field, fieldIndex) => {
              const foundField = fields.get(fieldIndex);
              return (
                <tr key={field}>
                  <td className='ldap-role__cell'>
                    <Field
                      anyValue
                      className='form-control'
                      component={
                        fieldIndex === 0 &&
                        foundField.get('groupName') === 'any'
                          ? RoleTextField
                          : SelectInputField
                      }
                      disabled={
                        fieldIndex === 0 &&
                        foundField.get('groupName') === 'any'
                      }
                      externalError={
                        groupErrors.has(String(fieldIndex))
                          ? `${groupErrors.get(String(fieldIndex))}Group`
                          : null
                      }
                      inputSize={100}
                      inputValue={get(
                        msg,
                        'configurations.roleAssignments.anyUser',
                      )}
                      label={get(
                        msg,
                        'configurations.roleAssignments.groupName',
                      )}
                      msg={msg}
                      name={`${field}.groupName`}
                      isLoading={groupDataLoading === fieldIndex}
                      menuRenderer={groupMenuRenderer(
                        msg,
                        groupDataLoading === fieldIndex,
                        get(
                          msg,
                          'configurations.roleAssignments.noResultsFound',
                        ),
                        inputValue,
                      )}
                      noResultsText={get(msg, ['selectForm', 'noResultsFound'])}
                      onSelectBlur={this.handleGroupBlur}
                      onChange={this.handleGroupChange(fieldIndex)}
                      onSelectFocus={this.handleGroupFocus(fieldIndex)}
                      onInputChange={this.handleGroupFilterChange(fieldIndex)}
                      options={groupOptions}
                    />
                    {fieldIndex === 0 && (
                      <span className='help-block'>
                        {get(msg, 'configurations.roleAssignments.anyUserHelp')}
                      </span>
                    )}
                  </td>
                  <td className='ldap-role__cell'>
                    <Field
                      block
                      component={SelectField}
                      components={{
                        Option: RolesSelectOption,
                      }}
                      msg={msg}
                      name={`${field}.roleId`}
                      onChange={this.handleFieldChange}
                      options={rolesOptions}
                      styles={{
                        clearIndicator: (base) => ({
                          ...base,
                          padding: '6px 8px',
                        }),
                        control: (base) => ({ ...base, minHeight: 34 }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: '6px 8px',
                        }),
                      }}
                    />
                  </td>
                  <td>
                    {fieldIndex !== 0 ? (
                      <Button
                        variant='tertiary'
                        className='ml-lgu-5'
                        type='button'
                        icon='fa-trash-can'
                        onClick={this.handleRemoveRole(fieldIndex)}
                        size='xxs'
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div>
          <Button
            variant='secondary'
            icon='fa-plus'
            type='button'
            className='mt-lgu-3'
            onClick={this.handleAddRole}
          >
            {get(msg, 'configurations.roleAssignments.btnAdd')}
          </Button>
        </div>
        {error && typeof error === 'string' && (
          <FormErrorMessage>
            {get(msg, ['validation', error], 'Error')}
          </FormErrorMessage>
        )}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  groupData: makeSelectGroupData(),
  groupDataLoading: makeSelectGroupDataLoading(),
  roles: makeSelectRoles(),
});

function mapDispatchToProps(dispatch) {
  return {
    onGroupSearchDataChange: (args) => dispatch(groupSearchDataChange(args)),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RenderRoleAssignments);
